import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import DynamicForm from "../UI/DynamicForm";
import DropdownField from "../UI/DropdownField";
import TemplateSearchDropdown from "../UI/TemplateSearchDropdown";
import { getPetitionTitles } from "../../actions/petitionActions";
import TitleInputDialog from "../../dialogs/TitleInputDialog";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import RegenerateConfirmationDialog from "../../dialogs/RegenerateConfirmationDialog";
import {
  Send,
  Search,
  FileText,
  Clock,
  ArrowRight,
  Palette,
  Menu,
} from "lucide-react";
import {
  generateDocumentStreamApi,
  getPetitionTypesApi,
  getDocumentSchemaApi,
} from "../../services/api/documentGenerationApi";
import {
  saveDraftingHistoryApi,
  updateDraftingHistoryDocumentApi,
} from "../../services/api/draftingHistoryApi";
import { parseSSEStream } from "../../utils/HelperFunction/utils";

const PetitionForm = ({
  isCollapsed,
  toggleSidebar,
  setGeneratedPetition,
  setIsGenerating,
  isGenerating,
  readOnly,
  setReadOnly,
  isEditing,
  setIsEditing,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const newPetitionRequest = useSelector((state) => state.newPetitionRequest);
  const { isClicked } = newPetitionRequest;

  const initializeFormData = (fields) => {
    const data = {};

    fields.forEach((field) => {
      if (field.type === "list" && Array.isArray(field.fields)) {
        // Add each nested field to this object
        field.fields.forEach((nestedField) => {
          data[nestedField.name] = nestedField.value || "";
        });
      } else {
        // For non-list fields, assign default value or empty string
        data[field.name] = field.value || "";
      }
    });

    return data;
  };

  const [selectedPetitionType, setSelectedPetitionType] = useState("");
  const [formConfig, setFormConfig] = useState({});
  const [petitionTypes, setPetitionTypes] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [openTitleInputModal, setOpenTitleInputModal] = useState(false);
  const [tempChatId, setTempChatId] = useState("");
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [formData, setFormData] = useState(() => {
    return initializeFormData(formConfig?.form?.fields || []);
  });

  const selectedPetitionState = useSelector((state) => state.selectedPetition);
  const { selectedPetition } = selectedPetitionState;
  const handleSaveTitleInput = async (title, isConfirmed, editing = false) => {
    if (!title) return;
    if ((!isConfirmed && selectedPetition) || editing) {
      setOpenConfirmationModal(true);
      return;
    }
    setOpenConfirmationModal(false);
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    setIsGenerating(true);
    setReadOnly(true);
    const newChatId = tempChatId ? tempChatId : uuidv4();
    const currentTime = dayjs().format("DD-MM-YYYY");
    const date = formData.Date;
    let updatedFormData;
    if (date) {
      // Parse the date using dayjs
      const day = dayjs(date).date().toString();
      const year = dayjs(date).year().toString();
      const monthName = dayjs(date).format("MMMM");

      updatedFormData = {
        ...formData,
        user_id: userInfo._id,
        chat_id: selectedPetition ? selectedPetition?.chat_id : newChatId,
        timecreated: currentTime,
        Category: selectedPetition
          ? selectedPetition?.Category
          : selectedPetitionType.Name,
        Title: title,
        Day: day,
        Month: monthName,
        Year: year,
      };
    } else {
      updatedFormData = {
        ...formData,
        user_id: userInfo._id,
        chat_id: selectedPetition ? selectedPetition?.chat_id : newChatId,
        timecreated: currentTime,
        Category: selectedPetition
          ? selectedPetition?.Category
          : selectedPetitionType.Name,
        Title: title,
      };
    }
    const petitionName = selectedPetition
      ? selectedPetition?.Category
      : selectedPetitionType.Name;
    setTempChatId(newChatId);
    try {
      const readableStream = await generateDocumentStreamApi(
        petitionName,
        updatedFormData,
      );
      let html = "";
      let metadata = {};
      await parseSSEStream(readableStream, ({ type, data }) => {
        if (type === "html") html = data.html;
        if (type === "done") metadata = data.metadata || {};
      });

      const filledForm = metadata.filled_form;
      const generatedPetition = {
        html,
        metadata,
        form_metadata: filledForm
          ? { id: filledForm.title, fields: filledForm.fields }
          : undefined,
      };

      const chatId = selectedPetition ? selectedPetition?.chat_id : newChatId;
      let savedHistoryId = selectedPetition?._id;
      try {
        if (selectedPetition?._id) {
          const updateResponse = await updateDraftingHistoryDocumentApi(
            "petition",
            {
              _id: selectedPetition._id,
              user_id: userInfo._id,
              Document: html,
              Title: title,
              form_metadata: generatedPetition.form_metadata,
            },
          );
          savedHistoryId =
            updateResponse?.data?.data?._id ?? selectedPetition._id;
        } else {
          const saveResponse = await saveDraftingHistoryApi("petition", {
            Category: petitionName,
            timecreated: currentTime,
            chat_id: chatId,
            user_id: userInfo._id,
            Title: title,
            Document: html,
            form_metadata: generatedPetition.form_metadata,
          });
          savedHistoryId = saveResponse?.data?.data?._id;
        }
      } catch (saveError) {
        console.warn("Failed to save petition drafting history.", saveError);
      }

      setGeneratedPetition(generatedPetition);
      dispatch({
        type: "SET_SELECTED_PETITION",
        payload: {
          ...generatedPetition.metadata,
          _id: savedHistoryId,
          Title: title,
          Category: petitionName,
          chat_id: chatId,
          form_metadata: generatedPetition.form_metadata,
        },
      });

      setIsGenerating(false);
      setReadOnly(true);
      dispatch(getPetitionTitles("petition", userInfo?._id));
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Generate petition on petition drafting page",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
      setReadOnly(true);
      setIsEditing(false);
    } catch (error) {
      toast.error("The petition could not be saved. Something went wrong.");
      setIsGenerating(false);
      setReadOnly(false);
      console.log(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const getPetitionTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const response = await getPetitionTypesApi();
      if (response.status === 200) {
        setIsLoadingTypes(false);
        const petitionTypesData = response.data;

        const updatedPetitionTypes = Object.entries(
          petitionTypesData?.forms || {},
        ).map(([endpoint, name]) => ({
          Name: name,
          Endpoint: endpoint,
        }));

        const sortedPetitionTypes = updatedPetitionTypes.sort((a, b) =>
          a.Name.localeCompare(b.Name),
        );

        setPetitionTypes(sortedPetitionTypes);
      }
    } catch (error) {
      setIsLoadingTypes(false);
      console.log(error);
    }
  };

  const handleLoadTemplate = async (value) => {
    // if (!selectedPetitionType) return;
    setIsFormLoading(true);
    try {
      const response = await getDocumentSchemaApi(value.Name);
      if (response.status === 200) {
        const schema = response.data;
        setFormConfig({ form: { id: schema.title, fields: schema.fields } });
        setIsFormLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleRadioChange = (e, fieldName) => {
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: e.target.value,
    }));
  };

  const validateFields = (fields, formData) => {
    const missingFields = [];

    const isRequired = (field) =>
      field.required === true || field.required === "true";

    fields.forEach((field) => {
      if (field.type === "list" && Array.isArray(field.fields)) {
        // Recursively validate nested fields in case of "list"
        field.fields.forEach((nestedField) => {
          if (isRequired(nestedField) && !formData[nestedField.name]) {
            missingFields.push(nestedField);
          }
        });
        // const nestedMissing = validateFields(field.fields, formData);
      } else if (isRequired(field) && !formData[field.name]) {
        // Validate non-list fields
        missingFields.push(field);
      }
    });

    return missingFields;
  };

  const generatePetition = (regenerate) => {
    // Validate required fields
    const missingFields = validateFields(
      formConfig?.form?.fields || [],
      formData,
    );

    if (missingFields?.length > 0) {
      toast.info("Please fill all the required fields.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    } else {
      if (regenerate) {
        setOpenConfirmationModal(true);
      } else {
        setOpenTitleInputModal(true);
      }
    }
  };

  const handleSelectTemplate = (value) => {
    setSelectedPetitionType(value || "");
    setReadOnly(false);
    setGeneratedPetition("");
    setTempChatId("");
    setFormData({});
    if (!value) {
      setFormConfig({});
      return;
    }
    handleLoadTemplate(value);
  };

  useEffect(() => {
    if (isClicked) {
      setSelectedPetitionType("");
      setFormConfig({});
      setFormData({});
      dispatch({ type: "NEW_PETITION_RESET" });
      setReadOnly(false);
      setIsEditing(false);
    }
  }, [isClicked, dispatch]);

  useEffect(() => {
    getPetitionTypes();
  }, []);
  useEffect(() => {
    if (selectedPetition) {
      setFormConfig({ form: selectedPetition?.form_metadata });
      setTempChatId("");
      setReadOnly(true);
    }

    if (selectedPetition?.form_metadata) {
      const initialData = initializeFormData(
        selectedPetition?.form_metadata?.fields,
      );
      setFormData(initialData);
    }
  }, [selectedPetition]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Petition Drafting</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isMobileView
            ? { width: "100%" }
            : {
                flexBasis: isCollapsed ? "35%" : "29%",
                flexShrink: 0,
                transition: "flex-basis 0.3s ease",
              }),
          height: "100%",
          boxSizing: "border-box",
          margin: "12px",
          backgroundColor: "#f5f5f5",
          borderRadius: "12px",
          border: "1px solid #F1F5F9",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          fontFamily:
            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              padding: "20px 24px",
              borderBottom: "1px solid #F1F5F9",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "0px",
              }}
            >
              <IconButton
                size="small"
                sx={{
                  display: { xs: "flex", lg: "none" },
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                  "&:hover": {
                    backgroundColor: "#E0F2FE",
                  },
                }}
                onClick={toggleSidebar}
              >
                <Menu size={20} />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  backgroundColor: isEditing ? "#F0F9FF" : "#F3E8FF",
                  borderRadius: "12px",
                  color: isEditing ? "#0073A6" : "#9333EA",
                }}
              >
                {isEditing ? <FileText size={20} /> : <Palette size={20} />}
              </Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: isEditing ? "#12243C" : "#12243C",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {isEditing
                  ? "Edit Template"
                  : selectedPetition
                    ? "Preview Template"
                    : "Select Template"}
              </Typography>
            </Box>
          </Box>
          {/* Template Selection */}
          {!selectedPetition && (
            <Box
              sx={{
                padding: "20px 20px 5px 20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Searchable Dropdown */}
              <TemplateSearchDropdown
                label="Select a Template"
                options={petitionTypes}
                value={selectedPetitionType}
                onChange={(value) => handleSelectTemplate(value)}
                loading={isLoadingTypes}
                disabled={isLoadingTypes}
              />
            </Box>
          )}
          {/* Form Content */}
          {!isFormLoading &&
          selectedPetitionType &&
          Object.keys(selectedPetitionType).length > 0 &&
          Object.keys(formConfig).length !== 0 ? (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                padding: "24px",
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "3px",
                  "&:hover": {
                    background: "#94A3B8",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#12243C",
                  marginBottom: "24px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedPetitionType.Name}
              </Typography>
              <DynamicForm
                formConfig={formConfig}
                formData={formData}
                readOnly={readOnly}
                handleInputChange={handleInputChange}
                handleRadioChange={handleRadioChange}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "32px",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(135deg, #0073A6 0%, #005A8B 100%)",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "12px 24px",
                    boxShadow: "0 2px 8px rgba(0, 115, 166, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #005A8B 0%, #004A73 100%)",
                      boxShadow: "0 4px 12px rgba(0, 115, 166, 0.4)",
                    },
                    "&:disabled": {
                      background: "#E2E8F0",
                      color: "#94A3B8",
                      boxShadow: "none",
                    },
                  }}
                  endIcon={
                    isGenerating ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <Send size={18} />
                    )
                  }
                  disabled={isGenerating || readOnly}
                  onClick={() => {
                    generatePetition(isEditing ? true : false);
                  }}
                >
                  {isEditing ? "Regenerate" : "Generate"}
                </Button>
              </Box>
            </Box>
          ) : isFormLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 20px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#0073A6" }} />
            </Box>
          ) : selectedPetition ? (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                padding: "24px",
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "3px",
                  "&:hover": {
                    background: "#94A3B8",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#12243C",
                  marginBottom: "24px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedPetition?.Category || selectedPetitionType?.Name}
              </Typography>
              <DynamicForm
                formConfig={formConfig}
                readOnly={readOnly}
                formData={formData}
                handleInputChange={handleInputChange}
                handleRadioChange={handleRadioChange}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "32px",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(135deg, #0073A6 0%, #005A8B 100%)",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "12px 24px",
                    boxShadow: "0 2px 8px rgba(0, 115, 166, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #005A8B 0%, #004A73 100%)",
                      boxShadow: "0 4px 12px rgba(0, 115, 166, 0.4)",
                    },
                    "&:disabled": {
                      background: "#E2E8F0",
                      color: "#94A3B8",
                      boxShadow: "none",
                    },
                  }}
                  endIcon={
                    isGenerating ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <Send size={18} />
                    )
                  }
                  disabled={isGenerating || readOnly}
                  onClick={() => {
                    generatePetition(true);
                  }}
                >
                  Regenerate
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                padding: "40px 24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#F8FAFC",
                  borderRadius: "16px",
                  marginBottom: "24px",
                }}
              >
                <FileText size={32} color="#64748B" />
              </Box>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#475569",
                  textAlign: "center",
                  marginBottom: "8px",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                No Template Selected
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#94A3B8",
                  textAlign: "center",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Choose a template to begin drafting your petition
              </Typography>
            </Box>
          )}
        </Box>
        <TitleInputDialog
          open={openTitleInputModal}
          onClose={() => {
            setOpenTitleInputModal(false);
          }}
          onSave={(title) =>
            handleSaveTitleInput(title, false, isEditing ? true : false)
          }
        />
        <RegenerateConfirmationDialog
          open={openConfirmationModal}
          onClose={() => setOpenConfirmationModal(false)}
          onConfirm={() => {
            handleSaveTitleInput(
              selectedPetition ? selectedPetition?.Title : "",
              true,
              false,
            );
            setOpenConfirmationModal(false);
            setIsEditing(false);
          }}
          type={"petition"}
        />
      </Box>
    </>
  );
};

export default PetitionForm;
